'use client';

import * as Sentry from '@sentry/nextjs';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import React, { useRef, useState } from 'react';
import { emailRegex } from '@winging/util/helper/regex';
import { useTranslations } from 'next-intl';
import { LabeledInputStatus } from '@/shared/ui/LabeledInput';
import { useAuth } from '@/shared/providers/AuthProvider';
import SignIn from '@/features/auth/ui/SignIn';
import EmailMfaVerification from '@/features/auth/ui/EmailMfaVerification';
import { usePathname, useRouter } from '@/i18n/navigation';
import {
  buildSalesStudioSignUpPath,
  getSafeAuthReturnPath,
} from '@/features/auth/lib/authReturnPath';

interface Toast {
  id: string;
  message: string;
}

const SignInScreen = () => {
  const t = useTranslations('Auth');
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const salesStudioSignIn = pathname.includes('/sales-studio/auth/sign-in');
  const requestedReturnTo = searchParams.get('returnTo');
  const returnTo = requestedReturnTo
    ? getSafeAuthReturnPath(requestedReturnTo)
    : salesStudioSignIn
      ? '/sales-studio/dashboard'
      : '/dashboard';
  const { signIn, verifySignInMfa, resendSignInMfa } = useAuth();
  const mfaSubmittingRef = useRef(false);
  const mfaResendingRef = useRef(false);

  const [passwordInputType, setPasswordInputType] = useState('password');
  const [isPersistent, setIsPersistent] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    maskedEmail: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStatus, setMfaStatus] = useState<LabeledInputStatus>('');
  const [mfaMsg, setMfaMsg] = useState('');
  const [isMfaSubmitting, setIsMfaSubmitting] = useState(false);
  const [isMfaResending, setIsMfaResending] = useState(false);

  const [emailMsg, setEmailMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const [emailStatus, setEmailStatus] = useState<LabeledInputStatus>('');
  const [passwordStatus, setPasswordStatus] = useState<LabeledInputStatus>('');

  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (message: string) => {
    const id = Math.random().toString();
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => removeToast(id), 3000); // 3초 후 자동 제거
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const validate = () => {
    let valid = true;
    if (!email) {
      valid = false;
      setEmailMsg(t('이메일 주소가 입력되지 않았습니다'));
      setEmailStatus('error');
    } else if (!emailRegex.test(email)) {
      valid = false;
      setEmailMsg(t('이메일 형식으로 올바르게 입력해주세요'));
      setEmailStatus('error');
    } else {
      setEmailMsg('');
      setEmailStatus('');
    }
    if (!password) {
      valid = false;
      setPasswordMsg(t('비밀번호가 입력되지 않았습니다'));
      setPasswordStatus('error');
    } else {
      setPasswordMsg('');
      setPasswordStatus('');
    }
    return valid;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    try {
      const result = await signIn({
        email,
        password,
        isPersistent,
      });
      if (result.mfaRequired) {
        setMfaChallenge(result);
        setMfaCode('');
        setMfaStatus('');
        setMfaMsg('');
        return;
      }
      router.push(returnTo);
    } catch (e) {
      if (e?.response?.data?.message?.startsWith('invalid_password')) {
        setPasswordStatus('error');
        setPasswordMsg(t('비밀번호가 올바르지 않습니다'));
        const attemptCount =
          e.response.data.message.split('_')[
            e.response.data.message.split('_').length - 1
          ];
        showToast(
          `${t('비밀번호를 10회 잘못 입력하면 로그인이 1분간 제한됩니다')} (${attemptCount}/10)`
        );
      } else if (e?.response?.data?.message === 'not_found_user') {
        setEmailStatus('error');
        setEmailMsg(t('가입되지 않은 계정입니다'));
      } else if (e?.response?.data?.message === 'mfa_code_recently_sent') {
        showToast(t('인증번호가 이미 전송되었습니다'));
      } else {
        Sentry.captureException(e);
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
    }
  };

  const submitMfa = async () => {
    if (mfaSubmittingRef.current || mfaResendingRef.current) return;
    if (!mfaChallenge || !/^\d{6}$/.test(mfaCode)) {
      setMfaStatus('error');
      setMfaMsg(t('6자리 인증번호를 입력해 주세요'));
      return;
    }
    mfaSubmittingRef.current = true;
    setIsMfaSubmitting(true);
    try {
      await verifySignInMfa({
        challengeId: mfaChallenge.challengeId,
        code: mfaCode,
      });
      router.push(returnTo);
    } catch (e) {
      const message = e?.response?.data?.message;
      setMfaStatus('error');
      if (message === 'invalid_mfa_code') {
        setMfaMsg(t('인증번호가 올바르지 않습니다'));
      } else if (message === 'mfa_challenge_expired') {
        setMfaMsg(t('인증번호가 만료되었습니다'));
      } else if (message === 'mfa_too_many_attempts') {
        setMfaMsg(t('인증 시도 횟수를 초과했습니다'));
      } else if (message === 'mfa_verification_in_progress') {
        setMfaMsg(t('인증을 처리하고 있습니다'));
      } else {
        Sentry.captureException(e);
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
    } finally {
      mfaSubmittingRef.current = false;
      setIsMfaSubmitting(false);
    }
  };

  const resendMfa = async (): Promise<boolean> => {
    if (!mfaChallenge || mfaSubmittingRef.current || mfaResendingRef.current) {
      return false;
    }
    mfaResendingRef.current = true;
    setIsMfaResending(true);
    try {
      await resendSignInMfa({ challengeId: mfaChallenge.challengeId });
      setMfaCode('');
      setMfaStatus('');
      setMfaMsg('');
      showToast(t('인증번호가 재전송되었습니다'));
      return true;
    } catch (e) {
      const message = e?.response?.data?.message;
      if (message === 'mfa_code_recently_sent') {
        showToast(t('인증번호가 이미 전송되었습니다'));
      } else if (message === 'mfa_challenge_expired') {
        setMfaStatus('error');
        setMfaMsg(t('인증번호가 만료되었습니다'));
      } else {
        Sentry.captureException(e);
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
      return false;
    } finally {
      mfaResendingRef.current = false;
      setIsMfaResending(false);
    }
  };

  const ShowPasswordButton = (
    <button
      className={'main:right-[8px] relative right-[20px]'}
      onClick={() => {
        if (passwordInputType === 'password') {
          setPasswordInputType('text');
        } else {
          setPasswordInputType('password');
        }
      }}
    >
      <img
        src={
          passwordInputType === 'password'
            ? '/icons/hide-password.svg'
            : '/icons/show-password.svg'
        }
        alt={'show password button'}
      />
    </button>
  );
  return (
    <div className="base:px-0 flex min-h-screen w-full items-start px-[42px]">
      <div
        className={
          'base:flex hidden h-full min-h-screen w-1/2 flex-col items-center justify-center bg-gray-50'
        }
      >
        <Image
          src="/images/sign-in-winging.png"
          width={471}
          height={559}
          alt="WingingText Character"
          sizes="472px"
          className="h-auto w-full max-w-[472px]"
        />
        <div className={'text-heading3 mt-[-16px] font-[700] text-gray-800'}>
          {t('당신만의 AI 에이전트를 만들어 보세요')}
        </div>
        <div className={'mt-[31px] flex justify-center'}>
          <img
            src={
              'https://cdn.winging.app/static/builder/icons/winging-logotype-original.png'
            }
            alt={'WingingText 로고'}
            className={'h-[35px]'}
          />
        </div>
      </div>
      <div
        className={
          'tablet:pt-[34px] base:w-1/2 base:pt-[70px] relative flex h-full min-h-screen w-full justify-center pt-[0]'
        }
      >
        <div
          className={
            'tablet:max-w-[402px] main:max-w-[452px] relative flex w-full flex-col items-center justify-center'
          }
        >
          <h1
            className={
              'text-title2 main:text-heading3 w-full text-center font-[600]'
            }
          >
            {mfaChallenge ? t('이메일 인증') : t('로그인')}
          </h1>
          <div className={'main:mt-[60px] mt-[12px] w-full'}>
            {mfaChallenge ? (
              <EmailMfaVerification
                code={mfaCode}
                setCode={setMfaCode}
                maskedEmail={mfaChallenge.maskedEmail}
                status={mfaStatus}
                message={mfaMsg}
                submit={submitMfa}
                resend={resendMfa}
                isSubmitting={isMfaSubmitting}
                isResending={isMfaResending}
                back={() => {
                  setMfaChallenge(null);
                  setMfaCode('');
                }}
              />
            ) : (
              <SignIn
                emailStatus={emailStatus}
                email={email}
                emailMsg={emailMsg}
                setEmail={setEmail}
                passwordStatus={passwordStatus}
                password={password}
                passwordMsg={passwordMsg}
                passwordInputType={passwordInputType}
                ShowPasswordButton={ShowPasswordButton}
                isPersistent={isPersistent}
                setIsPersistent={setIsPersistent}
                submit={submit}
                setPassword={setPassword}
              />
            )}
            {!mfaChallenge && (
              <div className={'flex justify-center gap-[40px]'}>
                <a href={'/auth/password/request'} className={'text-gray-400'}>
                  {t('비밀번호 찾기')}
                </a>
                <a
                  href={
                    salesStudioSignIn
                      ? buildSalesStudioSignUpPath(returnTo)
                      : '/auth/sign-up'
                  }
                  className={'text-primary'}
                >
                  {t('회원가입')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="fixed bottom-5 left-[75%] z-50 flex translate-x-[-50%] flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="animate-fade-in-up text-label1 rounded-[4px] bg-black/80 px-[30px] py-[14px] font-[400] whitespace-nowrap text-white"
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SignInScreen;
